import { ILogger } from '@tdp/common/telemetry';
import {
  IAnalyticsService,
  IAnalyticsV2Filters,
  IAnalyticsUsageDataHook,
  AnalyticsDataFetchResult,
  AnalyticsMetricsV1DataFetchResult,
  OS,
  Area,
  Platform,
  Host,
  Capability,
  IMetricData,
  IAnalyticsV2APIPayload,
  IAnalyticsV2PayloadFilters,
  IAnalyticsV2APIResponse,
  AppPublishTypeFetchResult,
  IAppPublishingCdnStatus,
  IMetric,
  AnalyticsMetricsV1ActiveUserDataFetchResult,
  IAnalyticsV2PreconditionFailedError,
} from '@tdp/modules/apps';

export interface AppAnalyticsServiceDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export const analyticsV2Url = (appId: string, filter: IAnalyticsV2Filters) => {
  let url = `/analytics/v2/apps/:appId/usage`;
  url = url.replace(':appId', appId);
  url = `${url}?entityType=${filter.entityType}`;
  return url;
};

export const analyticsV2Payload = (
  params: IAnalyticsUsageDataHook,
  filter: IAnalyticsV2Filters
): IAnalyticsV2APIPayload => {
  const filters: Partial<IAnalyticsV2PayloadFilters> = {
    os: params.dependencyFilters.os ? filter.os : OS.AllUp,
    region: params.dependencyFilters.area ? filter.area : Area.AllUp,
    platform: params.dependencyFilters.platform
      ? filter.platform
      : Platform.AllUp,
    host: params.dependencyFilters.host ? filter.host : Host.AllUp,
    capability: params.dependencyFilters.capability
      ? filter.capability
      : Capability.AllUp,
  };
  const payload: IAnalyticsV2APIPayload = {
    payloads: [
      {
        filters: filters,
        groups: params.groups,
        metrics: params.metric,
      },
    ],
  };
  return payload;
};

export class AnalyticsService implements IAnalyticsService {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ logger, apiFetch }: AppAnalyticsServiceDeps) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }

  async getAnalyticsV2Data(
    appId: string,
    filter: IAnalyticsV2Filters,
    params: IAnalyticsUsageDataHook
  ): Promise<AnalyticsDataFetchResult> {
    const response = await this.apiFetch(analyticsV2Url(appId, filter), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(analyticsV2Payload(params, filter)),
    });
    if (response.status == 412) {
      const dto = (await response.json()) as unknown as IAnalyticsV2PreconditionFailedError;

      this.logger.error(
        'Precondition failed: %s | reason: %s',
        appId,
        dto.errorMessage
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'networkError',
        errorMessage: dto.errorMessage,
        preconditionError: dto,
      });
    }
    if (response.status != 200) {
      this.logger.error(
        'Network error when fetching analytics data for app id: %s | reason: %s',
        appId,
        response.statusText
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'networkError',
        errorMessage: response.statusText,
      });
    }
    // TODO: (astripa) Add parsing schema
    try {
      const dto = (await response.json()) as unknown as IAnalyticsV2APIResponse;
      return {
        type: 'success',
        analyticsData: dto,
      };
    } catch (error) {
      this.logger.error(
        'Encountered parsing errors with payload from service for app id: %s',
        appId
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'parsingError',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown parsing error',
      });
    }
  }

  async getMetricsV1Data(
    appDefinitionId: string,
    aggregationType: string,
    platform: string,
    operatingSystem: string,
    area: string
  ): Promise<AnalyticsMetricsV1DataFetchResult> {
    const getMetricsUrl = `/analytics/metrics?appDefinitionId=${appDefinitionId}&entityType=${aggregationType}
      &platform=${platform}&clientOsName=${operatingSystem}&area=${area}`;
    const response = await this.apiFetch(getMetricsUrl);
    if (response.status != 200) {
      this.logger.error(
        'Network error when fetching metrics data for app id: %s | reason: %s',
        appDefinitionId,
        response.statusText
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'networkError',
        errorMessage: response.statusText,
      });
    }
    // TODO: (astripa) Add parsing schema
    try {
      const dto = (await response.json()) as unknown as IMetricData;
      return {
        type: 'success',
        metricsData: dto,
      };
    } catch (error) {
      this.logger.error(
        'Encountered parsing errors with payload from service for app id: %s',
        appDefinitionId
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'parsingError',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown parsing error',
      });
    }
  }

  async getMetricsV1MonthlyActiveUsers(
    appDefinitionId: string
  ): Promise<AnalyticsMetricsV1ActiveUserDataFetchResult> {
    const getMonthlyActiveUsersUrl = `/analytics/activeUsers?appDefinitionId=${appDefinitionId}`;
    const response = await this.apiFetch(getMonthlyActiveUsersUrl);
    if (response.status != 200) {
      this.logger.error(
        'Network error when fetching active user data for app id: %s | reason: %s',
        appDefinitionId,
        response.statusText
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'networkError',
        errorMessage: response.statusText,
      });
    }
    // TODO: (astripa) Add parsing schema
    try {
      const dto = (await response.json()) as unknown as IMetric[];
      return {
        type: 'success',
        activeUserData: dto,
      };
    } catch (error) {
      this.logger.error(
        'Encountered parsing errors with payload from service for app id: %s',
        appDefinitionId
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'parsingError',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown parsing error',
      });
    }
  }

  async getAppPublishType(
    externalAppId: string
  ): Promise<AppPublishTypeFetchResult> {
    const response = await this.apiFetch(
      `/appStatusTracking/apps/${externalAppId}/cdnStatus`
    );
    if (response.status != 200) {
      this.logger.error(
        'Network error when fetching app publish type | reason: %s',
        response.statusText
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'networkError',
        errorMessage: response.statusText,
      });
    }

    try {
      const dto =
        (await response.json()) as unknown as IAppPublishingCdnStatus[];
      return {
        type: 'success',
        appPublishCDNStatus: dto,
      };
    } catch (error) {
      this.logger.error(
        'Encountered parsing errors with payload from service for app id: %s',
        externalAppId
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'parsingError',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown parsing error',
      });
    }
  }
}
